import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { isAuthenticated, getUserProfile } from './authService';

const baseUrl = 'https://ebakasi.onrender.com';

export default function ProductDetail({ products, cart, setCart }) {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGalleryImage, setActiveGalleryImage] = useState('');
  const [justAdded, setJustAdded] = useState(false);
  const [detailQuantity, setDetailQuantity] = useState("");
  
  // Review modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewError, setReviewError] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const lookupId = id || slug;
        
        // First try to find in passed products list by _id or slug
        const foundProduct = products?.find(p => p._id === lookupId || p.slug === lookupId);
        
        if (foundProduct) {
          setProduct(foundProduct);
          const galleryImages = [foundProduct.image, ...(foundProduct.images || [])];
          const uniqueImages = [...new Set(galleryImages.filter(img => img && img.trim()))];
          setActiveGalleryImage(uniqueImages[0] || generatePlaceholderSVG(foundProduct.name));
        } else {
          // Fetch from API if not in list
          // Try slug-based endpoint first if slug is available, otherwise use id
          const endpoint = slug ? `${baseUrl}/api/products/slug/${slug}` : `${baseUrl}/api/products/${id || slug}`;
          const res = await fetch(endpoint);
          const data = await res.json();
          if (res.ok) {
            setProduct(data);
            const galleryImages = [data.image, ...(data.images || [])];
            const uniqueImages = [...new Set(galleryImages.filter(img => img && img.trim()))];
            setActiveGalleryImage(uniqueImages[0] || generatePlaceholderSVG(data.name));
          } else {
            setError('Product not found');
          }
        }
      } catch (err) {
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, slug, products]);

  // Scroll to top when product changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, slug]);

  // JSON-LD structured data for SEO
  useEffect(() => {
    if (!product) return;

    const productUrl = product.slug
      ? `https://ebakasi.com/products/${product.slug}`
      : `https://ebakasi.com/product/${product._id}`;

    const jsonLd = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      image: product.image,
      description: product.description || `${product.name} - Industrial procurement from Ebakasi Industrial Uganda`,
      sku: product.sku,
      brand: {
        '@type': 'Brand',
        name: product.brand || 'Ebakasi'
      },
      offers: {
        '@type': 'Offer',
        price: product.priceRetail,
        priceCurrency: 'UGX',
        availability: product.stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: productUrl
      },
      url: productUrl
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [product]);

  const fetchReviews = async (productId) => {
    try {
      const res = await fetch(`${baseUrl}/api/reviews/${productId}`);
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    }
  };

  const openReviewModal = (product) => {
    setReviewForm({ rating: 5, comment: '' });
    setReviewError('');
    fetchReviews(product._id);
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    if (!isAuthenticated()) {
      setReviewError('Please log in to submit a review.');
      return;
    }

    setReviewLoading(true);
    setReviewError('');

    try {
      const res = await fetch(`${baseUrl}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product._id,
          rating: reviewForm.rating,
          comment: reviewForm.comment,
          buyerEmail: getUserProfile().email,
          buyerName: getUserProfile().name || 'Anonymous',
        }),
      });

      if (res.ok) {
        setReviewForm({ rating: 5, comment: '' });
        fetchReviews(product._id);
      } else {
        const data = await res.json();
        setReviewError(data.error || 'Failed to submit review');
      }
    } catch (err) {
      setReviewError('Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  const generatePlaceholderSVG = (name) => {
    const encodedName = encodeURIComponent(name || 'Product');
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="480" height="360" viewBox="0 0 480 360">
        <rect width="480" height="360" fill="#1e293b"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#cbd5e1" font-size="24" font-family="sans-serif">${encodedName}</text>
      </svg>
    `)}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#cbd5e1' }}>
        Loading product...
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
        {error || 'Product not found'}
      </div>
    );
  }

  const galleryImages = [product.image, ...(product.images || [])];
  const uniqueImages = [...new Set(galleryImages.filter(img => img && img.trim()))];
  
  // Calculate display price based on pricing tiers
  let displayPrice = Number(product.priceRetail) || 0;
  let priceLabel = 'UGX ';
  
  if (product.pricingTiers && Array.isArray(product.pricingTiers) && product.pricingTiers.length > 0) {
    const lowestTierPrice = Math.min(...product.pricingTiers.map(t => Number(t.unitPrice)));
    displayPrice = lowestTierPrice || displayPrice;
    priceLabel = 'From UGX ';
  }
  
  const cleanRenderPrice = displayPrice > 0 ? displayPrice : 38000;
  // High-performance B2B bulk-saving margin analysis metrics
  const tierPrices = (product.pricingTiers || []).map(t => Number(t.unitPrice) || 0).filter(p => p > 0);
  const lowestTierPrice = tierPrices.length ? Math.min(...tierPrices) : 0;
  const baseReferencePrice = Number(product.priceRetail) && Number(product.priceRetail) > 0 ? Number(product.priceRetail) : (tierPrices.length ? Math.max(...tierPrices) : 0);
  
  const bulkSavingPercent = baseReferencePrice > lowestTierPrice && lowestTierPrice > 0 ? Math.round(((baseReferencePrice - lowestTierPrice) / baseReferencePrice) * 100) : 0;
  
  const tierSaving = (unitPrice) => {
    const up = Number(unitPrice) || 0;
    return baseReferencePrice > up && up > 0 ? Math.round(((baseReferencePrice - up) / baseReferencePrice) * 100) : 0;
  };
  const discountedPrice = product.discountPercent > 0 
    ? Math.round(product.priceRetail * (1 - product.discountPercent / 100))
    : null;
  
  // Get similar products (same industrial_sector/asset_profile)
  const similarProducts = (products?.filter(p =>
    p._id !== product._id &&
    p.industrial_sector === product.industrial_sector &&
    p.asset_profile === product.asset_profile
  ).slice(0, 6)) || [];
  
  // Fallback to any products if not enough similar
  const fallbackProducts = similarProducts.length < 2 
    ? (products?.filter(p => p._id !== product._id).slice(0, 6)) || []
    : similarProducts;
  
  const displaySimilar = similarProducts.length >= 2 ? similarProducts : fallbackProducts;

  // Helper to render video
  const renderVideo = () => {
    if (!product.videoUrl) return null;
    
    const youtubeMatch = product.videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    const vimeoMatch = product.videoUrl.match(/vimeo\.com\/(\d+)/);
    
    if (youtubeMatch) {
      return (
        <iframe
          width="100%"
          height="300"
          src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
          title="Product Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ marginTop: '12px', borderRadius: '8px' }}
        />
      );
    }
    
    if (vimeoMatch) {
      return (
        <iframe
          width="100%"
          height="300"
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          title="Product Video"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ marginTop: '12px', borderRadius: '8px' }}
        />
      );
    }
    
    // Fallback for other URLs
    return (
      <a
        href={product.videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          marginTop: '12px',
          padding: '10px 16px',
          backgroundColor: '#ea580c',
          color: '#fff',
          textDecoration: 'none',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '600',
        }}
      >
        ▶ Watch demo video
      </a>
    );
  };

  // SEO Helmet metadata
  const sector = product.industrial_sector || 'General';
  const location = product.location || 'Kampala';
  const SITE_URL = 'https://ebakasi.com';
  const productUrl = product.slug
    ? `${SITE_URL}/products/${product.slug}`
    : `${SITE_URL}/product/${product._id}`;

  const bulkSectors = ['Construction & Building Materials', 'Plumbing & Climate/HVAC', 'Electrical & Automation'];
  const capitalSectors = ['Tools & Hardware', 'Agricultural & Machinery', 'Safety & Security', 'Garden & Outdoor Care'];

  let title = '';
  if (bulkSectors.includes(sector)) {
    title = `Bulk ${product.name} Supplier Uganda | ${sector} Price ${location}`;
  } else if (capitalSectors.includes(sector)) {
    title = `${product.name} Industrial Sourcing Uganda | ${sector} Supply ${location}`;
  } else {
    title = `${product.name} | Ebakasi Industrial Uganda`;
  }

  const description = `${product.name} — ${sector} supply in ${location}. Verified vendor, escrow-protected checkout, nationwide Uganda delivery. ${(product.description || '').slice(0, 120)}`;
  const firstImage = product.image || 'https://ebakasi.com/apple-touch-icon.png';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={productUrl} />
        <meta property="og:image" content={firstImage} />
      </Helmet>

    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          backgroundColor: '#334155',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '13px',
        }}
      >
        ← Back to Catalog
      </button>

      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid var(--border-line)',
        }}
      >
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (max-width: 768px) {
              .detail-modal-gallery {
                height: 250px !important;
              }
              .similar-product-card {
                min-width: 120px !important;
                max-width: 120px !important;
              }
            }
            @media (max-width: 480px) {
              .detail-modal-header {
                flex-direction: column !important;
                align-items: flex-start !important;
              }
              .similar-product-card {
                min-width: 100px !important;
                max-width: 100px !important;
              }
            }
          `
        }} />
        <div
          className="detail-modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            position: 'relative',
          }}
        >
          <div style={{ flex: 1 }}>
            <h2 style={{ color: 'var(--text-primary, #fff)', margin: '0 0 8px 0', fontSize: '20px', fontWeight: '800' }}>
              {product.name}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                Brand: {product.brand || 'Unbranded'}
              </span>
              {product.discountPercent > 0 && (
                <span
                  style={{
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700',
                  }}
                >
                  {product.discountPercent}% OFF
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Price Section */}
        <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border-line)' }}>
          {discountedPrice ? (
            <div>
              <span style={{ color: '#ef4444', fontSize: '24px', fontWeight: '800' }}>
                UGX {discountedPrice.toLocaleString()}
              </span>
              <span style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '16px', 
                textDecoration: 'line-through',
                marginLeft: '12px'
              }}>
                UGX {product.priceRetail.toLocaleString()}
              </span>
            </div>
          ) : (
            <span style={{ color: '#22c55e', fontSize: '24px', fontWeight: '800' }}>
              {priceLabel}{cleanRenderPrice.toLocaleString()}
            </span>
          )}
          
          {/* Tier Matrix Display */}
          {product.pricingTiers && Array.isArray(product.pricingTiers) && product.pricingTiers.length > 0 && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#94a3b8' }}>
              <div style={{ fontWeight: '700', marginBottom: '6px', color: '#f59e0b' }}>Volume Pricing:</div>
              {product.pricingTiers.map((tier, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border-line)' }}>
                  <span>{tier.minQty}-{tier.maxQty} units</span>
                  <span style={{ color: '#22c55e', fontWeight: '600' }}>UGX {Number(tier.unitPrice).toLocaleString()}/unit</span>
                        {(() => {
                          const s = tierSaving(tier.unitPrice);
                          return s > 0 ? (
                            <span style={{ color: "#f59e0b", fontSize: "11px", fontWeight: "800", marginLeft: "6px" }}>
                              (Save {s}%)
                            </span>
                          ) : null;
                        })()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gallery */}
        <div style={{ marginBottom: '20px' }}>
          <div
            className="detail-modal-gallery"
            style={{
              width: '100%',
              height: '350px',
              backgroundColor: 'var(--bg-main)',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '12px',
              border: '1px solid var(--border-line)',
            }}
          >
            <img
              src={activeGalleryImage || generatePlaceholderSVG(product.name)}
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = generatePlaceholderSVG(product.name);
              }}
            />
          </div>
          {uniqueImages.length > 1 && (
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
              {uniqueImages.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  onClick={() => setActiveGalleryImage(img)}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: activeGalleryImage === img ? '2px solid #ea580c' : '1px solid var(--border-line)',
                  }}
                />
              ))}
            </div>
          )}
          {renderVideo()}
        </div>

        {/* Key Features */}
        {product.keyFeatures && product.keyFeatures.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
              ✓ Key Features
            </h3>
            <ul style={{ color: '#cbd5e1', fontSize: '13px', paddingLeft: '20px', margin: 0 }}>
              {product.keyFeatures.map((feature, idx) => (
                <li key={idx} style={{ marginBottom: '6px' }}>{feature}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Description */}
        {product.description && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
              📝 Description
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
              {product.description}
            </p>
          </div>
        )}

        {/* Specifications Table */}
        {product.specifications && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
              📐 Specifications
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {product.specifications.size && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8', width: '40%' }}>Size</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.size}</td>
                  </tr>
                )}
                {product.specifications.measurementUnit && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Measurement Unit</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.measurementUnit}</td>
                  </tr>
                )}
                {product.specifications.weightKg && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Weight</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.weightKg} kg</td>
                  </tr>
                )}
                {product.specifications.technicalSpecs && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Technical Specs</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.technicalSpecs}</td>
                  </tr>
                )}
                {product.specifications.dimensions && (product.specifications.dimensions.length || product.specifications.dimensions.width || product.specifications.dimensions.height) && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Dimensions</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>
                      {[
                        product.specifications.dimensions.length && `${product.specifications.dimensions.length} cm`,
                        product.specifications.dimensions.width && `${product.specifications.dimensions.width} cm`,
                        product.specifications.dimensions.height && `${product.specifications.dimensions.height} cm`,
                      ].filter(Boolean).join(' × ')}
                    </td>
                  </tr>
                )}
                {product.specifications.material && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Material</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.material}</td>
                  </tr>
                )}
                {product.specifications.powerRating && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Power Rating</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.powerRating}</td>
                  </tr>
                )}
                {product.specifications.color && (
                  <tr style={{ borderBottom: '1px solid var(--border-line)' }}>
                    <td style={{ padding: '8px', color: '#94a3b8' }}>Color</td>
                    <td style={{ padding: '8px', color: '#cbd5e1' }}>{product.specifications.color}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Delivery & Returns */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-line, #334155)' }}>
          <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
            🚚 Delivery & Returns
          </h3>
          {product.deliveryEtaDays ? (
            <p style={{ color: '#cbd5e1', fontSize: '13px', margin: '0 0 8px 0' }}>
              Ebakasi Express — Estimated delivery: {product.deliveryEtaDays} days
            </p>
          ) : null}
          <p style={{ color: '#cbd5e1', fontSize: '13px', margin: 0 }}>
            {product.returnPolicy || 'Returns handled per Ebakasi buyer-protection policy.'}
          </p>
        </div>

        {/* Seller / Compliance Info */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-line, #334155)' }}>
          <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
            🏭 Seller & Compliance
          </h3>
          {product.manufacturerName && (
            <p style={{ color: '#cbd5e1', fontSize: '13px', margin: '0 0 4px 0' }}>
              Manufacturer: {product.manufacturerName}
            </p>
          )}
          {product.manufacturerAddress && (
            <p style={{ color: '#cbd5e1', fontSize: '13px', margin: '0 0 8px 0' }}>
              {product.manufacturerAddress}
            </p>
          )}
          {product.unbsCertified && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>🛡️</span>
              <span style={{ color: '#22c55e', fontSize: '13px', fontWeight: '600' }}>
                UNBS Certified
              </span>
              {product.qualityGrade && product.qualityGrade !== 'Not Rated' && (
                <span style={{ color: '#cbd5e1', fontSize: '12px' }}>
                  — Grade: {product.qualityGrade}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Reviews Summary */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-line, #334155)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#f59e0b', margin: 0, fontSize: '15px', fontWeight: '800' }}>
              ⭐ Reviews
            </h3>
            {product.reviewCount > 0 && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                ★ {product.averageRating.toFixed(1)} ({product.reviewCount})
              </span>
            )}
          </div>
          <button
            onClick={() => openReviewModal(product)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: 'var(--bg-card)',
              color: '#fff',
              border: '1px solid #475569',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            See all reviews / Write a review
          </button>
        </div>

        {/* Add to Cart */}
                {/* Quantity + Add to Cart */}  
        <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "24px" }}>  
          <input  
            type="number"  
            min="1"  
            value={detailQuantity}  
            onChange={(e) => setDetailQuantity(e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value, 10) || 1))}  
            style={{  
              width: "80px",  
              padding: "14px",  
              borderRadius: "8px",  
              border: "1px solid var(--border-line)",  
              background: "#0f172a",  
              color: "#fff",  
              textAlign: "center",  
              fontSize: "15px",  
              fontWeight: "700",  
            }}  
          />  
          <button  
            type="button"
            onClick={() => {  
              const qtyToAdd = Math.max(1, parseInt(detailQuantity, 10) || 1);  
              const existingIndex = cart.findIndex((c) => (c._id || c.id) === product._id);  
              if (existingIndex >= 0) {  
                const updated = [...cart];  
                updated[existingIndex].quantity = (Number(updated[existingIndex].quantity) || 0) + qtyToAdd;  
                setCart(updated);  
              } else {  
                setCart([...cart, { ...product, quantity: qtyToAdd, priceRetail: cleanRenderPrice }]);  
              }  
              setDetailQuantity("");
              setJustAdded(true);
              setTimeout(() => setJustAdded(false), 1500);  
            }}  
            style={{  
              flex: 1,  
              padding: "14px",  
              backgroundColor: justAdded ? "#15803d" : "#22c55e",
              opacity: justAdded ? 0.85 : 1,  
              color: "#fff",  
              border: "none",  
              borderRadius: "8px",  
              fontSize: "15px",  
              fontWeight: "700",  
              cursor: "pointer",  
            }}  
          >  
            {justAdded ? "✓ Added to Cart!" : `Add to Cart — UGX ${cleanRenderPrice.toLocaleString()}`}  
          </button>  
        </div>

        {/* Similar Products */}
        {displaySimilar.length > 0 && (
          <div>
            <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '15px', fontWeight: '800' }}>
              You may also like
            </h3>
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
              {displaySimilar.map((similarProduct) => {
                // Calculate similar product price using tier-aware logic
                let similarPrice = Number(similarProduct.priceRetail) || 0;
                if (similarProduct.pricingTiers && Array.isArray(similarProduct.pricingTiers) && similarProduct.pricingTiers.length > 0) {
                  const lowestTierPrice = Math.min(...similarProduct.pricingTiers.map(t => Number(t.unitPrice)));
                  similarPrice = lowestTierPrice || similarPrice;
                }
                const similarDisplayPrice = similarPrice > 0 ? similarPrice : 38000;
                const similarImage = (similarProduct.image && similarProduct.image.trim() !== "")
                  ? similarProduct.image
                  : (similarProduct.images && similarProduct.images.length > 0 && similarProduct.images[0] && similarProduct.images[0].trim() !== "")
                    ? similarProduct.images[0]
                    : generatePlaceholderSVG(similarProduct.name);
                
                return (
                  <div
                    key={similarProduct._id}
                    onClick={() => navigate(`/product/${similarProduct._id}`)}
                    className="similar-product-card"
                    style={{
                      minWidth: '140px',
                      maxWidth: '140px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--bg-main)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-line)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={similarImage}
                      alt={similarProduct.name}
                      style={{
                        width: '100%',
                        height: '100px',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = generatePlaceholderSVG(similarProduct.name);
                      }}
                    />
                    <div style={{ padding: '8px' }}>
                      <div style={{ color: '#fff', fontSize: '11px', fontWeight: '600', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {similarProduct.name}
                      </div>
                      <div style={{ color: '#22c55e', fontSize: '12px', fontWeight: '700' }}>
                        UGX {similarDisplayPrice.toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '95%',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: '1px solid var(--border-line)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#f59e0b', margin: 0, fontSize: '18px', fontWeight: '800' }}>
                Customer Reviews
              </h3>
              <button
                onClick={() => setShowReviewModal(false)}
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            {/* Review Form */}
            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-line)' }}>
              <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700' }}>
                Write a Review
              </h4>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: '#cbd5e1', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Rating
                </label>
                <select
                  value={reviewForm.rating}
                  onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'var(--bg-card)',
                    color: '#fff',
                    border: '1px solid var(--border-line)',
                    borderRadius: '4px',
                  }}
                >
                  {[5, 4, 3, 2, 1].map(r => (
                    <option key={r} value={r}>{'★'.repeat(r)}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: '#cbd5e1', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Comment
                </label>
                <textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  placeholder="Share your experience..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'var(--bg-card)',
                    color: '#fff',
                    border: '1px solid var(--border-line)',
                    borderRadius: '4px',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                />
              </div>
              {reviewError && (
                <p style={{ color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>{reviewError}</p>
              )}
              <button
                onClick={submitReview}
                disabled={reviewLoading}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#ea580c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: reviewLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {reviewLoading ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>

            {/* Reviews List */}
            <div>
              <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700' }}>
                Recent Reviews
              </h4>
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <div
                    key={review._id}
                    style={{
                      backgroundColor: 'var(--bg-main)',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: '1px solid var(--border-line)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '600' }}>
                          {review.buyerName || 'Anonymous'}
                        </span>
                        <span style={{ color: '#22c55e', fontSize: '10px', fontWeight: '600' }}>
                          ✓ Verified Purchase
                        </span>
                      </div>
                      <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                        {'★'.repeat(review.rating)}
                      </span>
                    </div>
                    {review.comment && (
                      <p style={{ color: '#94a3b8', fontSize: '12px', margin: '4px 0 0 0' }}>
                        {review.comment}
                      </p>
                    )}
                    <span style={{ color: '#64748b', fontSize: '10px' }}>
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))
              ) : (
                <p style={{ color: '#64748b', fontSize: '12px' }}></p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
